import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import HeaderSection from './HeaderSection';
import TwitterSection from './TwitterSection';
import TikTokSection from './TikTokSection';
import InstagramSection from './InstagramSection';
import useSearchAccountGuide from '../Dashboard/useSearchAccountGuide';
import BottomSearchSection from './BottomSearchSection';

const UserTestimonials: React.FC = () => {
  const { t } = useTranslation('common');
  const { onSearchAccount, searchAccountLoading, renderPrivateAccountModal, renderAddAccountModal } =
    useSearchAccountGuide();

  // 创建引用以便滚动到对应部分
  const twitterRef = useRef<HTMLDivElement>(null);
  const tiktokRef = useRef<HTMLDivElement>(null);
  const instagramRef = useRef<HTMLDivElement>(null);

  // 当前激活的区域
  const [activeSection, setActiveSection] = useState<'twitter' | 'tiktok' | 'instagram'>('twitter');
  const [searchStr, setSearchStr] = useState('');

  // 滚动到指定部分
  const scrollToSection = (section: 'twitter' | 'tiktok' | 'instagram') => {
    // 不再立即更新激活状态，让激活状态完全由滚动位置决定
    let ref = twitterRef;
    if (section === 'tiktok') ref = tiktokRef;
    if (section === 'instagram') ref = instagramRef;

    if (ref.current) {
      const yOffset = -100; // 偏移量，避免被固定头部遮挡
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleSearchValueChanged = (value?: string) => {
    setSearchStr(value ?? '');
  };

  const handleSearchClick = (value?: string) => {
    onSearchAccount(value);
  };

  const bottomInfo = {
    title: t('userTestimonials.searchCtaTitle', { defaultValue: 'Your All-in-One Instagram Activity Tracker' }),
    desc: t('userTestimonials.searchCtaDesc', {
      defaultValue:
        'Turn public Instagram data into personal insights. Track follows, likes, and trends to understand real connections and engagement patterns.',
    }),
  };

  // 使用滚动监听来更新激活状态，确保只有一个按钮处于激活状态
  useEffect(() => {
    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + 150; // 考虑头部偏移

      const sections = [
        { ref: twitterRef, name: 'twitter' as const },
        { ref: tiktokRef, name: 'tiktok' as const },
        { ref: instagramRef, name: 'instagram' as const },
      ];

      // 从上到下遍历所有区域，找出当前应该激活的区域
      let currentSection: 'twitter' | 'tiktok' | 'instagram' = 'twitter';

      for (const section of sections) {
        if (section.ref.current) {
          const sectionTop = section.ref.current.offsetTop;

          // 如果滚动位置已经超过了这个区域的顶部，则更新当前区域
          if (scrollPosition >= sectionTop) {
            currentSection = section.name;
          }
        }
      }

      // 只有当激活区域真正改变时才更新状态，避免不必要的重渲染
      setActiveSection((prev) => {
        if (prev !== currentSection) {
          return currentSection;
        }
        return prev;
      });
    };

    // 初始化时更新一次
    updateActiveSection();

    // 监听滚动事件，使用节流优化性能
    let timeoutId: NodeJS.Timeout;
    const handleScroll = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(updateActiveSection, 50); // 50ms 节流
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // 清理事件监听器
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1200px] mx-auto">
        <div className="min-h-screen  p-0">
          {/* 头部区域：标题、副标题、导航按钮 */}
          <HeaderSection onNavigate={scrollToSection} activeSection={activeSection} />

          {/* Twitter 卡片区域 */}
          <div ref={twitterRef} id="twitter-section" className="scroll-mt-[100px] max-md:scroll-mt-20">
            <TwitterSection />
          </div>

          {/* TikTok 视频卡片区域 */}
          <div ref={tiktokRef} id="tiktok-section" className="scroll-mt-[100px] max-md:scroll-mt-20">
            <TikTokSection />
          </div>

          {/* Instagram 视频卡片区域 */}
          <div ref={instagramRef} id="instagram-section" className="scroll-mt-[100px] max-md:scroll-mt-20">
            <InstagramSection />
          </div>
        </div>
        <BottomSearchSection
          bottomInfo={bottomInfo}
          loading={searchAccountLoading}
          searchVal={searchStr}
          inputVisible={false}
          onValueChanged={handleSearchValueChanged}
          onSearchClick={handleSearchClick}
        />
        {renderAddAccountModal()}
        {renderPrivateAccountModal()}
      </div>
    </div>
  );
};

export default UserTestimonials;
